import * as React from 'react';
import Table from 'antd/lib/table';
import Result from 'antd/lib/result';
import Button from 'antd/lib/button';
import Skeleton from 'antd/lib/skeleton';
import Empty from 'antd/lib/empty';
import Spin from 'antd/lib/spin';
import { MehOutlined, LoadingOutlined } from '@ant-design/icons';
import { SummaryViewContextValue } from '@/utils/hooks/useSummaryView';
import { TabularLayoutType } from '@/types/summaryView';
import { makeDraggable } from '@/helpers/summaryViewHelpers';
import { antGrey } from '@/components/styles/globalStyles';
import type { DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
    arrayMove,
    horizontalListSortingStrategy,
    verticalListSortingStrategy,
    SortableContext,
} from '@dnd-kit/sortable';
import {
    DragIndexContext,
    DragIndexState,
    TableHeaderCell,
    TableBodyCell,
    TableBodyRow,
} from '@/components/orgSummary/DraggableTableComponents';

interface SummaryTableProps {
    summary: SummaryViewContextValue;
}

export const SummaryTable: React.FC<SummaryTableProps> = ({ summary }) => {
    const {
        preferences,
        error: { shouldDisplayFetchFailed, setShouldDisplayFetchFailed },
        fetchSummaryViewBatched,
        loading: { isLoading, isFetchingNextPage, isBatchProcessing },
        tableData,
        pagination: { hasMoreDestinations, loadMoreDestinations },
        summaryViewResponse,
        updatePreferences,
    } = summary;

    const { tabularLayout, selectedMetricKeys, selectedVersions, selectedDestinationIds } = preferences;
    const { standardColumns, compactConfig, filteredRows } = tableData;

    const isCompactLayout = tabularLayout === TabularLayoutType.Compact;
    const tableDataSource = isCompactLayout ? compactConfig.rows : filteredRows;

    const hasActualData = filteredRows.length > 0;

    const hasFetchCompleted = summaryViewResponse !== undefined || shouldDisplayFetchFailed;

    const showSkeletonInEmptyState = !hasFetchCompleted || (!hasActualData && isLoading);

    const [dragIndex, setDragIndex] = React.useState<DragIndexState>({ active: -1, over: -1 });

    const draggableStandardColumns = React.useMemo(() => makeDraggable(standardColumns), [standardColumns]);

    const skeletonDataSource = React.useMemo(() => {
        if (!showSkeletonInEmptyState) return tableDataSource;
        return Array.from({ length: 1 }).map((_, index) => ({
            key: `skeleton-${index}`,
            isSkeleton: true,
        }));
    }, [showSkeletonInEmptyState, tableDataSource]);

    const columnsWithSkeleton = React.useMemo(() => {
        if (!showSkeletonInEmptyState) return standardColumns;
        return standardColumns.map((col) => ({
            ...col,
            render: (value: any, record: any, index: number) => {
                if (record.isSkeleton) {
                    return <Skeleton.Input active size="small" style={{ width: '100%' }} />;
                }
                return col.render ? col.render(value, record, index) : value;
            },
        }));
    }, [showSkeletonInEmptyState, standardColumns]);

    const showHeader = !(showSkeletonInEmptyState && isCompactLayout);

    const sortableColumnKeys = React.useMemo(() => {
        if (isCompactLayout) {
            return compactConfig.columns
                .filter((col) => !('fixed' in col && col.fixed))
                .map((col) => col.key as string);
        }
        return draggableStandardColumns.filter((col) => !col.fixed).map((col) => col.key as string);
    }, [isCompactLayout, draggableStandardColumns, compactConfig.columns]);

    const sortableRowKeys = React.useMemo(() => {
        const source = isCompactLayout ? compactConfig.rows : filteredRows;
        return source.map((row) => row.key);
    }, [isCompactLayout, filteredRows, compactConfig.rows]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    const onDragEnd = React.useCallback(
        ({ active, over }: DragEndEvent) => {
            if (active.id !== over?.id) {
                const dragType = active.data.current?.type as 'column' | 'row';
                const keys = dragType === 'row' ? sortableRowKeys : sortableColumnKeys;
                const activeIndex = keys.findIndex((key) => key === active.id);
                const overIndex = keys.findIndex((key) => key === over?.id);
                if (activeIndex === -1 || overIndex === -1) return;
                const newOrder = arrayMove(keys, activeIndex, overIndex);

                if (isCompactLayout) {
                    const key = dragType === 'row' ? 'metricRowOrder' : 'destinationColumnOrder';
                    updatePreferences({ dragOrderPreferences: { compact: { [key]: newOrder } } });
                } else {
                    const key = dragType === 'row' ? 'rowOrder' : 'metricColumnOrder';
                    updatePreferences({ dragOrderPreferences: { standard: { [key]: newOrder } } });
                }
            }
            setDragIndex({ active: -1, over: -1 });
        },
        [sortableColumnKeys, sortableRowKeys, isCompactLayout, updatePreferences],
    );

    const onDragOver = React.useCallback(
        ({ active, over }: DragOverEvent) => {
            const dragType = (active.data.current?.type as 'column' | 'row') ?? 'column';
            const keys = dragType === 'row' ? sortableRowKeys : sortableColumnKeys;
            const activeIndex = keys.findIndex((key) => key === active.id);
            const overIndex = keys.findIndex((key) => key === over?.id);
            setDragIndex({
                active: active.id,
                over: over?.id,
                direction: overIndex > activeIndex ? 'right' : 'left',
                dragType,
            });
        },
        [sortableColumnKeys, sortableRowKeys],
    );

    const sentinelRef = React.useRef<HTMLDivElement>(null);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const loadMoreRef = React.useRef(loadMoreDestinations);
    const isLoadingRef = React.useRef(false);

    loadMoreRef.current = loadMoreDestinations;

    React.useEffect(() => {
        isLoadingRef.current = isFetchingNextPage || isBatchProcessing;
    }, [isFetchingNextPage, isBatchProcessing]);

    // Standard view: IntersectionObserver with viewport
    React.useEffect(() => {
        if (isCompactLayout) return undefined;
        if (!hasFetchCompleted) return undefined;

        const sentinel = sentinelRef.current;
        if (!sentinel) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && hasMoreDestinations && !isLoadingRef.current) {
                    isLoadingRef.current = true;
                    loadMoreRef.current();
                }
            },
            {
                root: null,
                rootMargin: '200px',
                threshold: 0,
            },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMoreDestinations, hasFetchCompleted, isCompactLayout]);

    // Compact view: scroll event listener on Table's internal scroll container
    React.useEffect(() => {
        if (!isCompactLayout) return undefined;
        if (!hasFetchCompleted) return undefined;

        const container = scrollContainerRef.current;
        if (!container) return undefined;

        const tableScrollContainer = container.querySelector('.ant-table-content');
        if (!tableScrollContainer) return undefined;

        const checkAndLoadMore = () => {
            if (!hasMoreDestinations || isLoadingRef.current) return;

            const { scrollLeft, scrollWidth, clientWidth } = tableScrollContainer;
            const scrollThreshold = 200;

            const isNearEnd = scrollLeft + clientWidth >= scrollWidth - scrollThreshold;
            const noScrollNeeded = scrollWidth <= clientWidth;

            if (isNearEnd || noScrollNeeded) {
                isLoadingRef.current = true;
                loadMoreRef.current();
            }
        };

        checkAndLoadMore();

        tableScrollContainer.addEventListener('scroll', checkAndLoadMore);
        return () => tableScrollContainer.removeEventListener('scroll', checkAndLoadMore);
    }, [hasMoreDestinations, hasFetchCompleted, isCompactLayout]);

    const loadingIndicator = isFetchingNextPage && (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: isCompactLayout ? '0 20px' : '20px 0 40px 0',
                position: isCompactLayout ? 'absolute' : 'relative',
                right: isCompactLayout ? 0 : undefined,
                top: isCompactLayout ? '50%' : undefined,
                transform: isCompactLayout ? 'translateY(-50%)' : undefined,
            }}
        >
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </div>
    );

    const draggableCompactColumns = React.useMemo(() => makeDraggable(compactConfig.columns), [compactConfig.columns]);

    const finalColumns = React.useMemo(() => {
        if (showSkeletonInEmptyState) return columnsWithSkeleton;
        return isCompactLayout ? draggableCompactColumns : draggableStandardColumns;
    }, [
        showSkeletonInEmptyState,
        columnsWithSkeleton,
        isCompactLayout,
        draggableCompactColumns,
        draggableStandardColumns,
    ]);

    const renderTable = () => (
        <Table
            size="small"
            pagination={false}
            showHeader={showHeader}
            rowKey={(row) => row.key}
            scroll={{ x: 'max-content' }}
            style={{ fontSize: 6, flexShrink: 0 }}
            columns={finalColumns}
            dataSource={skeletonDataSource}
            locale={{
                emptyText: <Empty description="No data" />,
            }}
            components={{
                header: { cell: TableHeaderCell },
                body: { row: TableBodyRow, cell: TableBodyCell },
            }}
        />
    );

    return (
        <div
            ref={scrollContainerRef}
            style={{
                height: '100%',
                width: '100%',
                overflow: 'auto',
                position: 'relative',
            }}
        >
            {shouldDisplayFetchFailed ?
                <Result
                    icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                    subTitle="Error fetching summary view"
                    extra={
                        <Button
                            onClick={() => {
                                setShouldDisplayFetchFailed(false);
                                fetchSummaryViewBatched({
                                    destinationIds: selectedDestinationIds,
                                    versions: selectedVersions,
                                    properties: selectedMetricKeys,
                                });
                            }}
                        >
                            Try again
                        </Button>
                    }
                />
            :   <>
                    <div style={{ display: isCompactLayout ? 'flex' : 'block' }}>
                        <DndContext
                            sensors={sensors}
                            onDragEnd={onDragEnd}
                            onDragOver={onDragOver}
                            collisionDetection={closestCenter}
                        >
                            <SortableContext items={sortableColumnKeys} strategy={horizontalListSortingStrategy}>
                                <SortableContext items={sortableRowKeys} strategy={verticalListSortingStrategy}>
                                    <DragIndexContext.Provider value={dragIndex}>
                                        {renderTable()}
                                    </DragIndexContext.Provider>
                                </SortableContext>
                            </SortableContext>
                        </DndContext>
                        {/* Sentinel for standard view only - compact view uses scroll detection */}
                        {!isCompactLayout && (
                            <div
                                ref={sentinelRef}
                                style={{
                                    width: '100%',
                                    height: '20px',
                                    flexShrink: 0,
                                }}
                            />
                        )}
                    </div>
                    {loadingIndicator}
                </>
            }
        </div>
    );
};
